import {
  ColDef,
  ICellRendererParams,
  ValueFormatterParams,
} from "ag-grid-community";
import CustomizedStatus from "components/Custom/CustomizedStatus";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatDate } from "utils/Date";
import {
  emptyFormatter,
  numberFormatter,
  statusValueFormatter,
} from "utils/Formatter/Global";

export const useBillingNoteByDeliveryItemReportColumnDefs = () => {
  const [columnDefs, setColumnDefs] = useState<ColDef[]>([]);
  const { t } = useTranslation();

  useEffect(() => {
    const formatRelatedUsers = (users: any[]) => {
      if (!users?.length) return "-";

      return users
        .map((u) => [u.first_name, u.last_name].filter(Boolean).join(" "))
        .join(", ");
    };

    const formatVatType = (type: string) => {
      switch (type) {
        case "no_vat":
          return "ไม่มี";
        case "vat_0":
          return "0%";
        case "vat_7":
          return "7%";
        default:
          return "-";
      }
    };
    setColumnDefs([
      // หัวเอกสาร
      {
        field: "unique_id",
        headerName: "เลขที่เอกสาร (ใบวางบิล)",
        filter: "agTextColumnFilter",
        valueFormatter: (params: ValueFormatterParams) =>
          emptyFormatter(params.value),
        minWidth: 200,
        flex: 1,
      },
      {
        field: "aggrid_status",
        headerName: "สถานะ",
        filter: "agSetColumnFilter",
        filterParams: {
          values: ["draft", "wait_payment", "fully_payment", "cancelled"],
          valueFormatter: (params: ValueFormatterParams) =>
            statusValueFormatter(params.value),
        },
        cellRenderer: (params: ICellRendererParams) => (
          <CustomizedStatus status={params.value} />
        ),
        cellStyle: {
          display: "flex",
          justifyContent: "flex-center",
          width: "100%",
        },
        minWidth: 200,
        flex: 1,
      },
      {
        field: "company_type",
        headerName: "บริษัท",
        filter: "agSetColumnFilter",
        filterParams: {
          values: ["forwarder", "transport"],
          valueFormatter: (params: ValueFormatterParams) =>
            params.value === "forwarder" ? "ฟอร์เวิดเดอร์" : "ทรานสปอร์ต",
        },
        valueFormatter: (params: ValueFormatterParams) => {
          if (!params.value) return "-";
          return params.value === "forwarder" ? "ฟอร์เวิดเดอร์" : "ทรานสปอร์ต";
        },
        minWidth: 200,
        flex: 1,
      },
      {
        field: "created_date",
        headerName: "วันที่สร้าง",
        filter: "agDateColumnFilter",
        valueFormatter: (params: ValueFormatterParams) =>
          params.value ? formatDate(params.value) : "-",
        minWidth: 200,
        flex: 1,
      },
      {
        field: "issue_date",
        headerName: "วันที่ออกเอกสาร (ใบวางบิล)",
        filter: "agDateColumnFilter",
        valueFormatter: (params: ValueFormatterParams) =>
          params.value ? formatDate(params.value) : "-",
        minWidth: 200,
        flex: 1,
        sort: "desc",
      },
      {
        field: "due_date",
        headerName: "วันครบกำหนด",
        filter: "agDateColumnFilter",
        valueFormatter: (params: ValueFormatterParams) =>
          params.value ? formatDate(params.value) : "-",
        minWidth: 200,
        flex: 1,
      },
      {
        field: "related_user_list",
        headerName: "ผู้เกี่ยวข้อง",
        filter: false,
        sortable: false,
        valueFormatter: (params: ValueFormatterParams) => {
          return formatRelatedUsers(params.value);
        },
        minWidth: 200,
        flex: 1,
      },
      // ลูกค้า
      {
        field: "customer_unique_id",
        headerName: "รหัสลูกค้า",
        filter: "agTextColumnFilter",
        valueFormatter: (params: ValueFormatterParams) =>
          emptyFormatter(params.value),
        minWidth: 200,
        flex: 1,
      },
      {
        field: "customer_name",
        headerName: "ชื่อลูกค้า",
        filter: "agTextColumnFilter",
        sortable: false,
        valueFormatter: (params: ValueFormatterParams) =>
          emptyFormatter(params.value),
        minWidth: 200,
        flex: 1,
      },
      {
        field: "tax_id",
        headerName: "เลขประจำตัวผู้เสียภาษี",
        filter: false,
        sortable: false,
        valueFormatter: (params: ValueFormatterParams) =>
          emptyFormatter(params.value),
        minWidth: 200,
        flex: 1,
      },
      // รายการ
      {
        field: "dt_index",
        headerName: "รายการ",
        filter: false,
        sortable: false,
        valueFormatter: (params: ValueFormatterParams) =>
          emptyFormatter(params.value),
        minWidth: 100,
        flex: 1,
      },
      // เอกสาร DT
      {
        field: "dt_unique_id",
        headerName: "เลขที่เอกสาร (ใบงานจัดส่ง)",
        filter: "agTextColumnFilter",
        sortable: false,
        valueFormatter: (params: ValueFormatterParams) =>
          emptyFormatter(params.value),
        minWidth: 200,
        flex: 1,
      },
      {
        field: "job_no",
        headerName: "Job No.",
        filter: "agTextColumnFilter",
        sortable: false,
        valueFormatter: (params: ValueFormatterParams) =>
          emptyFormatter(params.value),
        minWidth: 150,
        flex: 1,
      },
      {
        field: "dt_issue_date",
        headerName: "วันที่ออกเอกสาร (ใบงานจัดส่ง)",
        filter: "agDateColumnFilter",
        valueFormatter: (params: ValueFormatterParams) =>
          params.value ? formatDate(params.value) : "-",
        minWidth: 200,
        flex: 1,
      },
      {
        field: "dt_vehicle_license_plate",
        headerName: "ทะเบียนรถ",
        filter: "agTextColumnFilter",
        sortable: false,
        valueFormatter: (params: ValueFormatterParams) =>
          emptyFormatter(params.value),
        minWidth: 150,
        flex: 1,
      },
      {
        field: "dt_vehicle_type",
        headerName: "ประเภทรถ",
        valueFormatter: (params: ValueFormatterParams) =>
          emptyFormatter(params.value),
        filter: "agTextColumnFilter",
        sortable: false,
        minWidth: 150,
        flex: 1,
      },
      {
        // 86d488a5h — the value frozen onto this billing note, not the trip's
        // current price, so the report always agrees with the document.
        // `0` is a legal price, so the guard tests for null explicitly rather
        // than using a truthiness check like the amount column below.
        field: "dt_fuel_price",
        headerName: "ราคาน้ำมัน",
        filter: false,
        sortable: false,
        valueFormatter: (params: ValueFormatterParams) =>
          params.value === null || params.value === undefined
            ? "-"
            : numberFormatter(params.value),
        cellStyle: { display: "flex", justifyContent: "flex-end" },
        headerClass: "ag-end-header",
        minWidth: 150,
        flex: 1,
      },
      // รายการ DT
      {
        field: "income_item_unique_id",
        headerName: "รหัสสินค้า",
        filter: "agTextColumnFilter",
        sortable: false,
        valueFormatter: (params: ValueFormatterParams) =>
          emptyFormatter(params.value),
        minWidth: 200,
        flex: 1,
      },
      {
        field: "income_item_name",
        headerName: "ชื่อสินค้า",
        filter: "agTextColumnFilter",
        sortable: false,
        valueFormatter: (params: ValueFormatterParams) =>
          emptyFormatter(params.value),
        minWidth: 200,
        flex: 1,
      },
      {
        field: "income_item_pre_vat_amount",
        headerName: "จำนวนเงิน",
        filter: false,
        sortable: false,
        valueFormatter: (params: ValueFormatterParams) => {
          if (params.value) return numberFormatter(params.value);
          return "0.00";
        },
        cellStyle: { display: "flex", justifyContent: "flex-end" },
        headerClass: "ag-end-header",
        minWidth: 150,
        flex: 1,
      },
      {
        field: "description",
        headerName: "รายละเอียด",
        filter: false,
        sortable: false,
        valueFormatter: (params: ValueFormatterParams) =>
          emptyFormatter(params.value),
        minWidth: 200,
        flex: 1,
      },
      // Summary
      {
        field: "pre_discount_amount",
        headerName: "รวม",
        filter: false,
        sortable: false,
        valueFormatter: (params: ValueFormatterParams) => {
          if (params.value) return numberFormatter(params.value);
          return "0.00";
        },
        cellStyle: { display: "flex", justifyContent: "flex-end" },
        headerClass: "ag-end-header",
        minWidth: 150,
        flex: 1,
      },
      {
        field: "discount",
        headerName: "ส่วนลด (แยกภาษี) (มูลค่า)",
        filter: false,
        sortable: false,
        minWidth: 200,
        flex: 1,
        valueFormatter: (params: ValueFormatterParams) => {
          if (params.value) return numberFormatter(params.value);
          return "0.00";
        },
        cellStyle: { display: "flex", justifyContent: "flex-end" },
        headerClass: "ag-end-header",
      },
      {
        field: "discount_type",
        headerName: "ส่วนลด (แยกภาษี) (ประเภท)",
        filter: false,
        sortable: false,
        valueFormatter: (params: ValueFormatterParams) =>
          params.value === "baht" ? "บาท" : "%",
        minWidth: 200,
        flex: 1,
      },
      {
        field: "discount_thb",
        headerName: "ส่วนลด (แยกภาษี) (บาท)",
        filter: false,
        sortable: false,
        valueFormatter: (params: ValueFormatterParams) => {
          if (params.value) return numberFormatter(params.value);
          return "0.00";
        },
        cellStyle: { display: "flex", justifyContent: "flex-end" },
        headerClass: "ag-end-header",
        minWidth: 200,
        flex: 1,
      },
      {
        field: "post_discount_amount",
        headerName: "มูลค่าหลังหักส่วนลด",
        filter: false,
        sortable: false,
        valueFormatter: (params: ValueFormatterParams) => {
          if (params.value) return numberFormatter(params.value);
          return "0.00";
        },
        cellStyle: { display: "flex", justifyContent: "flex-end" },
        headerClass: "ag-end-header",
        minWidth: 200,
        flex: 1,
      },
      {
        field: "vat_type",
        headerName: "ภาษีมูลค่าเพิ่มรวม (มูลค่า)",
        filter: false,
        sortable: false,
        valueFormatter: (params: ValueFormatterParams) => {
          return formatVatType(params.value);
        },
        cellStyle: { display: "flex", justifyContent: "flex-end" },
        headerClass: "ag-end-header",
        minWidth: 200,
        flex: 1,
      },
      {
        field: "vat_amount",
        headerName: "ภาษีมูลค่าเพิ่มรวม (บาท)",
        filter: false,
        sortable: false,
        valueFormatter: (params: ValueFormatterParams) => {
          if (params.value) return numberFormatter(params.value);
          return "0.00";
        },
        cellStyle: { display: "flex", justifyContent: "flex-end" },
        headerClass: "ag-end-header",
        minWidth: 200,
        flex: 1,
      },
      {
        field: "net_amount",
        headerName: "มูลค่ารวมสุทธิ",
        filter: false,
        sortable: false,
        valueFormatter: (params: ValueFormatterParams) => {
          if (params.value) return numberFormatter(params.value);
          return "0.00";
        },
        cellStyle: { display: "flex", justifyContent: "flex-end" },
        headerClass: "ag-end-header",
        minWidth: 200,
        flex: 1,
      },
      {
        field: "withholding_tax_percent",
        headerName: "ภาษีหัก ณ ที่จ่าย (มูลค่า)",
        filter: false,
        sortable: false,
        valueFormatter: (params: ValueFormatterParams) => {
          return `${numberFormatter(params.value)}%`;
        },
        cellStyle: { display: "flex", justifyContent: "flex-end" },
        headerClass: "ag-end-header",
        minWidth: 200,
        flex: 1,
      },
      {
        field: "withholding_tax_amount",
        headerName: "ภาษีหัก ณ ที่จ่าย (บาท)",
        filter: false,
        sortable: false,
        valueFormatter: (params: ValueFormatterParams) => {
          if (params.value) return numberFormatter(params.value);
          return "0.00";
        },
        cellStyle: { display: "flex", justifyContent: "flex-end" },
        headerClass: "ag-end-header",
        minWidth: 200,
        flex: 1,
      },
      {
        field: "total_amount",
        headerName: "จำนวนเงินชำระทั้งสิ้น",
        filter: false,
        valueFormatter: (params: ValueFormatterParams) => {
          if (params.value) return numberFormatter(params.value);
          return "0.00";
        },
        cellStyle: { display: "flex", justifyContent: "flex-end" },
        headerClass: "ag-end-header",
        minWidth: 200,
        flex: 1,
      },
      {
        field: "remark",
        headerName: "หมายเหตุเอกสาร",
        filter: false,
        sortable: false,
        valueFormatter: (params: ValueFormatterParams) =>
          emptyFormatter(params.value),
        minWidth: 200,
        flex: 1,
      },
    ]);
  }, [t]);

  return columnDefs;
};
